import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, forwardRef, inject, ViewEncapsulation, afterNextRender, computed, effect, input, isSignal, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { PickerItemComponent } from '@workbuddy/ui/v2/components/button/picker/button.picker-item.component';
import { ContentModel as ButtonContentModel } from '@workbuddy/ui/v2/components/primitives/content';
import { SearchComponent } from '@workbuddy/ui/fields/search/search.component';
import { EmptyMessageComponent } from '@jms/ui/ui.empty.message';
import { DragPlaceholderComponent } from '@workbuddy/ui/components/drag.placeholder';
import { SettingsPanelMode, SettingsPanelSelectOption } from '../model';
import { PickerOptionId, toSelectedArray } from './picker.selection.utils';
import { settingsPanelPickerBridgeStyles, settingsPanelPickerStyleId } from './settings.panel.picker.theme';

export interface SettingsPanelPickerViewConfig {
  options: SettingsPanelSelectOption[] | import('@angular/core').Signal<SettingsPanelSelectOption[]>;
  mode: 'single' | 'multi';
  searchable?: boolean;
  reorderable?: boolean;
  selectedLabel?: string;
  availableLabel?: string;
  emptyMessage?: string;
  emptyIcon?: string;
  onSearch?: (query: string) => void;
}

interface PickerGroupOption {
  option: SettingsPanelSelectOption;
  content: ButtonContentModel;
}

interface PickerGroup {
  label: string | null;
  options: PickerGroupOption[];
}

@Component({
  selector: 'settings-panel-picker',
  standalone: true,
  imports: [CommonModule, DragDropModule, PickerItemComponent, SearchComponent, EmptyMessageComponent, DragPlaceholderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './settings.panel.picker.component.html',
  styleUrls: ['./settings.panel.picker.component.less'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SettingsPanelPickerComponent), multi: true }],
})
export class SettingsPanelPickerComponent implements ControlValueAccessor {

  private readonly document = inject(DOCUMENT);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly $searchEl = viewChild(SearchComponent, { read: ElementRef });
  private readonly $items = viewChildren(PickerItemComponent);
  private keyManager: ActiveDescendantKeyManager<PickerItemComponent> | null = null;

  // The id of the currently-highlighted item — bound to the search input via
  // `aria-activedescendant` so screen readers track the highlight.
  protected readonly $activeDescendantId = signal<string | null>(null);

  // Where to land the highlight on the next re-render. Used after toggle so
  // the highlight stays at the same position in the source list rather than
  // jumping to the moved item in the destination list.
  private _pendingActiveIndex: number | null = null;

  public $config = input.required<SettingsPanelPickerViewConfig>({ alias: 'config' });
  public $mode = input<SettingsPanelMode>(SettingsPanelMode.Sidebar, { alias: 'mode' });
  public $active = input<boolean>(true, { alias: 'active' });

  protected readonly $isCommandPalette = computed(() => this.$mode() === SettingsPanelMode.CommandPalette);
  protected readonly $showAvailableHeader = computed(() => this.$config().mode === 'multi' || !!this.$config().availableLabel);

  protected readonly $searchQuery = signal<string>('');
  protected $selected = signal<PickerOptionId[]>([]);
  private $selectedSet = computed(() => new Set(this.$selected()));

  private _onChange: (value: PickerOptionId | PickerOptionId[]) => void = () => {};
  private _onTouched: () => void = () => {};
  private wasActive = false;
  private pendingPositionReset = false;

  protected $options = computed<SettingsPanelSelectOption[]>(() => {

    const config = this.$config();
    const options = config.options;
    return isSignal(options) ? options() : options;
  });

  protected $groups = computed<PickerGroup[]>(() => {

    const config = this.$config();
    const options = this.$availableOptions();
    const hasUserGroups = options.some(o => o.group);

    if (!hasUserGroups) return [{ label: null, options: this.buildGroupOptions(options, config.mode) }];

    return this.groupOptions(options, null, config.mode);
  });

  protected $availableOptions = computed(() => {

    const selectedSet = this.$selectedSet();
    const query = this.$searchQuery().toLowerCase().trim();
    const config = this.$config();
    return this.$options().filter(o => (config.mode === 'single' || !selectedSet.has(o.id)) && (!query || o.label?.toLowerCase().includes(query)));
  });

  protected $selectedOptions = computed(() => {

    const options = this.$options();
    const optionMap = new Map(options.map(o => [o.id, o]));
    return this.$selected().map(id => optionMap.get(id)).filter(Boolean);
  });

  protected $selectedGroupOptions = computed(() => {

    return this.buildGroupOptions(this.$selectedOptions(), 'multi');
  });

  private buildGroupOptions(options: SettingsPanelSelectOption[], mode: 'single' | 'multi'): PickerGroupOption[] {

    const checkable = mode === 'single' ? 'radio' : 'checkbox';

    return options.map(option => ({
      option,
      content: this.createButtonContent(option, checkable),
    }));
  }

  private createButtonContent(option: SettingsPanelSelectOption, checkable: 'radio' | 'checkbox'): ButtonContentModel {

    const color = option.color ? { value: option.color, tooltip: option.label } : null;

    return {
      mode: 'item',
      growLabel: false,
      label: option.label,
      labelMode: 'text',
      loading: false,
      avatar: option.avatar ?? null,
      color,
      colorShape: 'square',
      iconLeft: option.icon ?? null,
      iconLeftColor: null,
      iconRight: null,
      iconRightColor: null,
      iconRightFallback: null,
      count: null,
      checkable,
      checked: false,
    };
  }

  public constructor() {

    DesignSystem.Mount(this.document, settingsPanelPickerStyleId, settingsPanelPickerBridgeStyles);

    // After the panel slides in and the search input renders, focus it so
    // typing/arrows work without an extra Tab.
    afterNextRender(() => this.focusSearchAfterSlide());

    // Re-create the ActiveDescendantKeyManager whenever rendered items change
    // (search filter / selection move). The first item is highlighted by default.
    effect(() => {
      const items = this.$items();
      this.keyManager?.destroy();
      if (items.length === 0) {
        this.keyManager = null;
        this.$activeDescendantId.set(null);
        return;
      }
      const manager = new ActiveDescendantKeyManager<PickerItemComponent>([...items]).withWrap();
      this.keyManager = manager;
      const sub = manager.change.subscribe(idx => {
        const item = this.$items()[idx];
        this.$activeDescendantId.set(item?.getId() ?? null);
      });
      this.destroyRef.onDestroy(() => sub.unsubscribe());
      untracked(() => {
        // Honor a pending index (e.g. from toggle) so the highlight stays at
        // the user's spot in the source list instead of resetting to first.
        const target = this._pendingActiveIndex !== null
          ? Math.min(this._pendingActiveIndex, items.length - 1)
          : 0;
        this._pendingActiveIndex = null;
        manager.setActiveItem(Math.max(0, target));
        const item = this.$items()[manager.activeItemIndex ?? 0];
        this.$activeDescendantId.set(item?.getId() ?? null);
      });
      this.applyPendingPositionReset();
    });

    effect(() => {
      const active = this.$active();
      if (active && !this.wasActive) {
        this.pendingPositionReset = true;
        this.applyPendingPositionReset();
      }
      this.wasActive = active;
    });
  }

  private focusSearchAfterSlide(): void {

    if (this.$config().searchable === false) return;

    const track = this.host.nativeElement.closest('.settings-panel-track') as HTMLElement | null;
    if (!track) {
      this.focusSearch();
      return;
    }

    const styles = window.getComputedStyle(track);
    const hasTransition = parseFloat(styles.transitionDuration) > 0;
    if (!hasTransition) {
      this.focusSearch();
      return;
    }

    const onEnd = (e: TransitionEvent) => {
      if (e.target !== track || e.propertyName !== 'transform') return;
      track.removeEventListener('transitionend', onEnd);
      this.focusSearch();
    };
    track.addEventListener('transitionend', onEnd);
    this.destroyRef.onDestroy(() => track.removeEventListener('transitionend', onEnd));
  }

  private focusSearch(): void {

    this.getSearchInput()?.focus({ preventScroll: true });
  }

  private resetScrollPosition(): void {

    const scrollRoot = this.host.nativeElement.querySelector<HTMLElement>('.sp-picker-list-root');
    if (!scrollRoot) return;

    scrollRoot.scrollTop = 0;
  }

  private applyPendingPositionReset(): void {

    if (!this.pendingPositionReset || !this.$active()) return;
    const scrollRoot = this.host.nativeElement.querySelector<HTMLElement>('.sp-picker-list-root');
    if (!scrollRoot || !this.$items().length) return;

    this.pendingPositionReset = false;
    this._pendingActiveIndex = null;
    this.keyManager?.setActiveItem(0);
    this.$activeDescendantId.set(this.$items()[0]?.getId() ?? null);
    this.document.defaultView?.requestAnimationFrame(() => this.resetScrollPosition());
  }

  // ── CVA ─────────────────────────────────────────────────────────────

  public writeValue(value: PickerOptionId | PickerOptionId[]): void {

    this.$selected.set(toSelectedArray(value));
  }

  public registerOnChange(fn: (value: PickerOptionId | PickerOptionId[]) => void): void {

    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this._onTouched = fn;
  }

  // ── Actions ─────────────────────────────────────────────────────────

  protected isSelected(option: SettingsPanelSelectOption): boolean {

    return this.$selectedSet().has(option.id);
  }

  protected onSearch(query: string) {

    this.$searchQuery.set(query);
    this.$config().onSearch?.(query);
  }

  protected toggle(option: SettingsPanelSelectOption) {

    const config = this.$config();
    const wasSelected = this.$selectedSet().has(option.id);
    let next: PickerOptionId[];

    if (config.mode === 'single') {
      next = [option.id];
    } else {
      const current = this.$selected();
      next = wasSelected ? current.filter(id => id !== option.id) : [...current, option.id];
    }

    // Compute where the highlight should land after the re-render. Toggling
    // moves an item between Selected and Available sections (total count
    // unchanged); we want the highlight to stay in the SAME section if possible.
    const currentIdx = this.keyManager?.activeItemIndex ?? 0;
    const selectedCountBefore = this.$selectedOptions().length;
    if (wasSelected) {
      // Removed from Selected — Selected count drops by one. Clamp to the new
      // selected boundary so highlight stays in Selected (or the new last index there).
      const newSelectedCount = selectedCountBefore - 1;
      this._pendingActiveIndex = newSelectedCount > 0
        ? Math.min(currentIdx, newSelectedCount - 1)
        : currentIdx;  // no Selected left — fall back to keeping index (lands in Available)
    } else {
      // Added to Selected — Selected gained one entry at the end, so Available
      // items below currentIdx shifted by +1 in the flat list. Adjust to keep
      // the highlight on the same Available item the user was hovering.
      this._pendingActiveIndex = currentIdx + 1;
    }

    this.$selected.set(next);
    this._onChange(config.mode === 'single' ? option.id : next);
    this._onTouched();
  }

  /**
   * Keydown on the search input — arrows move highlight, Enter/Space activate.
   * Letter keys and Backspace fall through to the input's native handling.
   */
  protected onSearchKeydown(event: KeyboardEvent): void {

    const manager = this.keyManager;
    if (!manager) return;

    switch (event.key) {

      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
        event.preventDefault();
        manager.onKeydown(event);
        return;

      case 'Enter':
      case ' ': {
        if (event.key === ' ' && !this.$isCommandPalette()) return;
        const idx = manager.activeItemIndex ?? -1;
        if (idx < 0) return;
        const flat = this.flattenRenderedOptions();
        if (idx < flat.length) {
          event.preventDefault();
          this.toggle(flat[idx]);
        }
        return;
      }
    }
  }

  private getSearchInput(): HTMLInputElement | null {

    return this.host.nativeElement.querySelector<HTMLInputElement>('.sp-picker-search input');
  }

  /** Flatten currently-rendered options to a single list matching $items() order. */
  private flattenRenderedOptions(): SettingsPanelSelectOption[] {

    const config = this.$config();
    const flat: SettingsPanelSelectOption[] = [];
    if (config.mode === 'multi') {
      for (const groupOption of this.$selectedGroupOptions()) {
        flat.push(groupOption.option);
      }
    }
    for (const group of this.$groups()) {
      for (const groupOption of group.options) {
        flat.push(groupOption.option);
      }
    }
    return flat;
  }

  protected onDrop(event: CdkDragDrop<PickerGroupOption[]>): void {

    if (event.previousIndex === event.currentIndex) return;

    const renderedIds = this.$selectedOptions().map(option => option.id);
    const hiddenIds = this.$selected().filter(id => !renderedIds.includes(id));
    const next = [...renderedIds];
    moveItemInArray(next, event.previousIndex, event.currentIndex);

    next.push(...hiddenIds);
    this.$selected.set(next);

    const config = this.$config();
    this._onChange(config.mode === 'single' ? next[0] : next);
    this._onTouched();
  }

  private groupOptions(options: SettingsPanelSelectOption[], defaultLabel: string | null, mode: 'single' | 'multi'): PickerGroup[] {

    const hasUserGroups = options.some(o => o.group);

    if (!hasUserGroups) return [{ label: defaultLabel, options: this.buildGroupOptions(options, mode) }];

    const groupMap = new Map<string, SettingsPanelSelectOption[]>();
    const ungrouped: SettingsPanelSelectOption[] = [];

    for (const option of options) {
      if (option.group) {
        const group = groupMap.get(option.group) ?? [];
        group.push(option);
        groupMap.set(option.group, group);
      } else {
        ungrouped.push(option);
      }
    }

    const groups: PickerGroup[] = [];
    if (ungrouped.length) groups.push({ label: defaultLabel, options: this.buildGroupOptions(ungrouped, mode) });
    for (const [label, opts] of groupMap) {
      groups.push({ label, options: this.buildGroupOptions(opts, mode) });
    }
    return groups;
  }
}
